import { useNavigate } from "react-router-dom";
import ButtonComponent from "../../../../components/button/button";
import { useState, useEffect } from "react";
import InputComponent from "../../../../components/input/input";
import { getMe, postUser } from "../../../../features/search/service/user-service";
import { getNonSuperAdminRoles, getRoles } from "../../../../features/search/service/role-service";
import { getCompaniesListSelect } from "../../../../features/search/service/company-service";
import { SelectOption } from "../../../../shared/types/select-options";
import { User } from "../../../../shared/types/user";
import SelectComponent from "../../../../components/input/select";
import { MenuTop } from '../../../../components/menu/top/menu-top';
import './styles/add.css';
import { ROLES } from "../../../../shared/consts/roles";

export const AdminUsersAdd = () => {
    const navigate = useNavigate();
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [roleId, setRoleId] = useState<string>('');
    const [companyId, setCompanyId] = useState<string>('');
    const [me, setMe] = useState<User | null>(null);
    const [roles, setRoles] = useState<SelectOption[]>([]);
    const [companies, setCompanies] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoadingData(true);
                setError('');
                
                const userData = await getMe();
                setMe(userData);

                if (!userData) {
                    throw new Error('Não foi possível carregar dados do usuário');
                }

                let rolesData: SelectOption[] = [];
                let companiesData: SelectOption[] = [];

                const userRoleDescription = userData?.role?.description?.toLowerCase().trim();
                const isSuperAdmin = userRoleDescription === ROLES.SUPER_ADMIN.toLowerCase();
                const isAdmin = userRoleDescription === ROLES.ADMIN.toLowerCase();
                const isCompany = userRoleDescription === 'empresa';

                if (isSuperAdmin) {
                    const [rolesResponse, companiesResponse] = await Promise.all([
                        getRoles(),
                        getCompaniesListSelect()
                    ]);
                    
                    rolesData = rolesResponse;
                    companiesData = companiesResponse;
                    
                } else if (isAdmin || isCompany) {
                    rolesData = await getNonSuperAdminRoles();
                    
                    if (userData?.company) {
                        companiesData = [{
                            id: userData.company.id,
                            label: userData.company.name || userData.company.description || 'Minha Empresa'
                        }];
                    } else {
                        throw new Error('Usuário sem empresa associada');
                    }
                } else {
                    throw new Error('Permissão insuficiente para adicionar usuários');
                }
                
                if (rolesData && rolesData.length > 0) {
                    setRoles(rolesData);
                    setRoleId(String(rolesData[0].id));
                } else {
                    setError('Nenhum nível de acesso disponível');
                }

                if (companiesData && companiesData.length > 0) {
                    setCompanies(companiesData);
                    setCompanyId(String(companiesData[0].id));
                } else {
                    setError('Nenhuma empresa disponível');
                }
                
            } catch (err: any) {
                console.error('Erro ao buscar dados iniciais:', err);
                setError(err.message || 'Erro ao carregar dados. Verifique suas permissões.');
            } finally {
                setLoadingData(false);
            }
        };

        fetchInitialData();
    }, []);

    const handleAddUser = async () => {
        if (!name || name.trim() === '') {
            alert('Por favor, preencha o nome');
            return;
        }

        if (!email || email.trim() === '') {
            alert('Por favor, preencha o email');
            return;
        }

        if (!password || password.trim() === '') {
            alert('Por favor, preencha a senha');
            return;
        }

        if (!roleId || roleId === '' || roleId === 'undefined') {
            alert('Por favor, selecione um nível de acesso');
            return;
        }

        if (!companyId || companyId === '' || companyId === 'undefined') {
            alert('Por favor, selecione uma empresa');
            return;
        }

        const payload = {
            name: name.trim(),
            email: email.trim(),
            password,
            roleId: roleId,
            companyId: companyId,
        };

        setLoading(true);

        try {
            const user = await postUser(payload);
            
            setName('');
            setEmail('');
            setPassword('');

            if (user) {
                navigate('/admin-painel/users');
            }
            
        } catch (error) {
            console.error('Erro ao adicionar usuário:', error);
            alert('Erro ao criar usuário. Verifique os dados e tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const isFormValid = () => {
        return (
            name.trim() !== '' &&
            email.trim() !== '' &&
            password.trim() !== '' &&
            roleId && roleId !== '' && roleId !== 'undefined' &&
            companyId && companyId !== '' && companyId !== 'undefined'
        );
    };

    if (loadingData) {
        return (
            <div>
                <MenuTop />
                <div className="add-user-container">
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        minHeight: '300px',
                        color: '#666'
                    }}>
                        <p>Carregando dados...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && roles.length === 0 && companies.length === 0) {
        return (
            <div>
                <MenuTop />
                <div className="add-user-container">
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center',
                        minHeight: '300px',
                        color: '#ff6b6b',
                        gap: '20px'
                    }}>
                        <h2>⚠️ Erro ao carregar dados</h2>
                        <p>{error}</p>
                        <ButtonComponent 
                            label="Voltar"
                            onClick={() => navigate('/admin-painel/users')}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <MenuTop />
            <div className="add-user-container">
                <h1>Adicionar Usuário</h1>

                <InputComponent
                    id="id-input-name"
                    label="Nome *"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                />
                
                <InputComponent
                    id="id-input-email"
                    label="Email *"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                />
                
                <InputComponent
                    id="id-input-password"
                    label="Senha *"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                />

                {companies.length > 0 ? (
                    <SelectComponent
                        label="Empresa *"
                        value={companyId || ''}
                        onChange={(value) => {
                            setCompanyId(String(value));
                        }}
                        options={companies}
                        styles={{ marginTop: '10px' }}
                    />
                ) : (
                    <p style={{ color: '#ff6b6b', fontSize: '14px', marginTop: '10px' }}>
                        ⚠️ Nenhuma empresa disponível. Verifique suas permissões.
                    </p>
                )}

                {roles.length > 0 ? (
                    <SelectComponent
                        label="Nível de Acesso *"
                        value={roleId || ''}
                        onChange={(value) => {
                            setRoleId(String(value));
                        }}
                        options={roles}
                        styles={{ marginTop: '10px' }}
                    />
                ) : (
                    <p style={{ color: '#ff6b6b', fontSize: '14px', marginTop: '10px' }}>
                        ⚠️ Nenhum nível de acesso disponível. Verifique suas permissões.
                    </p>
                )}

                <div className="button-container">
                    <ButtonComponent
                        label={loading ? "Criando..." : "Criar Usuário"}
                        onClick={handleAddUser}
                        disabled={loading || !isFormValid()}
                    />
                </div>

                <p style={{ color: '#999', fontSize: '12px', marginTop: '10px' }}>
                    * Campos obrigatórios
                </p>
            </div>
        </div>
    );
}